import React, { useMemo } from "react";
import { RightsFiltersParams } from "../rights/RightsFilters";
import { Button } from "../../components/Button";
import { Card } from "../../components/Card/Card";
import { CardBody } from "../../components/Card/CardBody";
import { Preloader } from "../../components/Preloader";
import { MemoizedTable } from "../../components/Table/Table";
import { useApiExecutor } from "../../hooks/useApiExecutor";
import { RightIssue, useRightsApi } from "../../api/useRightsApi";
import { rightsIssuesColumnsDefinitionShort } from "./RightsIssuesColumnsDefinition";
import { useTranslation } from "react-i18next";
import { ModalChangeShare } from "./ModalChangeShare";
import { useAdvancedState } from "../../hooks/use-advanced-state";
import { useContractors } from "../../contexts/ContractorsContext";
import { StringParam, useQueryParams } from "use-query-params";
import { RightsIssuesFilters } from "./RightsIssuesFilters";
import { PaginationComponent } from "../../components/Pagination";

export const RightsIssuesPage: React.FC = () => {
  const { t } = useTranslation();
  const api = useRightsApi();
  const { contractors } = useContractors();
  const [query] = useQueryParams({
    contractor_id: StringParam
  });
  const [shareState, setShareState, updateShareState] = useAdvancedState<{
    index: number;
    share: number;
    territory_shares: number[]
  } | null>(null);
  const { info, lastResponse, contents, setContents, fetch, fetchPage } = useApiExecutor<
    RightIssue,
    RightsFiltersParams
  >({
    isInitLoadingState: contractors?.length > 1 && query.contractor_id != null,
    showInPage: 100,
    fetchCallback: api.getRightsIssues
  });

  const handleModalClose = (share: number | null) => {
    if (share != null && shareState?.index != null) {
      setContents(prev => [
        ...prev.slice(0, shareState.index as number - 1),
        {
          ...prev[shareState.index as number],
          share,
          common_share: prev[shareState.index as number].common_share - (prev[shareState.index as number].share - share)
        },
        ...prev.slice(shareState.index as number + 1)
      ]);
    }

    setShareState(null);
  };

  const columnsDefinitions = useMemo(() => {
    return rightsIssuesColumnsDefinitionShort(t, ((s, ts, index) => updateShareState({
      index: index,
      share: s,
      territory_shares: ts
    }))) as any;
  }, []);

  return <>
    <div className={"w-full border-solid py-3 xl:px-8 sm:px-4 border-2 border-gray-200 rounded-r rounded-b"}>
      <div className={"mb-3"}>
        <RightsIssuesFilters
          isLoading={info.isPageLoading || info.isInitialLoading}
          isEmpty={(contents?.length ?? 0) === 0}
          // @ts-ignore
          customInfo={lastResponse?.period ?? null}
          onApply={fetch}
        />
      </div>
      <div className={"flex justify-between items-center flex-wrap mb-3 gap-2"}>
        <Card className={"w-full"}>
          <CardBody>
            {info?.isPageLoading || info?.isInitialLoading ? (
              <Preloader size={"3x"} className={"text-muted"} />
            ) : (
              <MemoizedTable
                data={contents}
                columnsDefinition={columnsDefinitions}
              />
            )}
          </CardBody>
        </Card>
      </div>
    </div>

    <br />
    {!info.isPageLoading && !info.isInitialLoading && (
      <PaginationComponent threshold={10} showInPage={100} info={info} onChange={fetchPage} />
    )}

    <ModalChangeShare
      payload={shareState?.share != null ? shareState as any : null}
      onClose={handleModalClose}
    />
  </>;
};
